import { Lender } from '@1delta/lender-registry'
import { rayAccumulator } from '../accumulator'
import {
  getAaveAssets,
  getAaveStyleProtocolTokenMap,
  LENDER_MODE_NO_MODE,
  toGenericPriceKey,
  toOracleKey,
} from '../../../assets'
import {
  BPS,
  computeUtilization,
  formatAaveRawApyToApr,
  parseRawAmount,
} from '../../../utils/parsing'
import {
  AaveV2GeneralPublicResponse,
  AaveV2TypeGetReserveConfigurationData,
  AaveV2TypeGetReserveDataIndexes,
} from '../../../types/lender/aave-v2-types'
import { AdditionalYields } from '../../../types'
import { GenericTokenList } from '../../../types'
import { Chain } from '@1delta/chain-registry'
import { createMarketUid } from '../../../utils'
import { lenderShortName } from '../../../utils/marketName'

export const getAaveV2ReservesDataConverter = (
  lender: string,
  chainId: string,
  prices: { [asset: string]: number },
  additionalYields: AdditionalYields,
  tokenList: GenericTokenList = {},
): [(data: any[]) => AaveV2GeneralPublicResponse | undefined, number] => {
  switch (lender) {
    /** AAVE V2 style with rewards from state */
    default: {
      const assetsToQuery = getAaveAssets(chainId, lender)
      const aaveTokenMap = getAaveStyleProtocolTokenMap(chainId, lender)

      const expectedNumberOfCalls = assetsToQuery.length * 2

      return [
        (data: any[]) => {
          if (data.length !== expectedNumberOfCalls) {
            return undefined
          }

          const resultReserves: any = {}

          for (let i = 0; i < assetsToQuery.length; i++) {
            const asset = assetsToQuery[i]
            const reserveData = data[i * 2]
            const configData = data[i * 2 + 1]

            const assetMeta = tokenList[asset]
            const decimals = assetMeta?.decimals ?? 18

            const totalStableDebt = parseRawAmount(
              reserveData?.[
                AaveV2TypeGetReserveDataIndexes.totalStableDebt
              ]?.toString(),
              decimals,
            )
            const totalVariableDebt = parseRawAmount(
              reserveData?.[
                AaveV2TypeGetReserveDataIndexes.totalVariableDebt
              ]?.toString(),
              decimals,
            )
            const liquidity = Number(
              parseRawAmount(
                reserveData?.[
                  AaveV2TypeGetReserveDataIndexes.availableLiquidity
                ]?.toString(),
                decimals,
              ),
            )
            const totalAToken =
              liquidity + Number(totalStableDebt) + Number(totalVariableDebt)

            const oracleKey =
              toOracleKey(assetMeta?.assetGroup) ??
              toGenericPriceKey(asset, chainId)
            const price = prices[oracleKey] ?? 0

            const totalDepositsUSD = Number(totalAToken) * price
            const totalDebtUSD = Number(totalVariableDebt) * price

            const collateralActive =
              configData?.[
                AaveV2TypeGetReserveConfigurationData.usageAsCollateralEnabled
              ]
            const borrowingEnabled =
              configData?.[
                AaveV2TypeGetReserveConfigurationData.borrowingEnabled
              ]

            const marketUid = createMarketUid(chainId, lender, asset)

            resultReserves[marketUid] = {
              marketUid,
              name: lenderShortName(lender) + ' ' + (assetMeta?.symbol ?? ''),
              poolId: asset,
              underlying: asset,
              asset: assetMeta,
              // token amounts
              totalDeposits: totalAToken,
              totalDebtStable: totalStableDebt,
              totalDebt: totalVariableDebt,
              totalLiquidity: liquidity,
              borrowLiquidity: liquidity,
              // USD amounts
              totalDepositsUSD,
              totalDebtStableUSD: Number(totalStableDebt) * price,
              totalDebtUSD,
              totalLiquidityUSD: liquidity * price,
              borrowLiquidityUSD: liquidity * price,
              utilization: computeUtilization(
                totalAToken,
                totalVariableDebt,
                totalStableDebt,
              ),
              accumulator: rayAccumulator(
                reserveData?.[AaveV2TypeGetReserveDataIndexes.liquidityIndex],
                reserveData?.[
                  AaveV2TypeGetReserveDataIndexes.variableBorrowIndex
                ],
              ),
              // rates
              depositRate: formatAaveRawApyToApr(
                reserveData?.[
                  AaveV2TypeGetReserveDataIndexes.liquidityRate
                ]?.toString(),
              ),
              variableBorrowRate: formatAaveRawApyToApr(
                reserveData?.[
                  AaveV2TypeGetReserveDataIndexes.variableBorrowRate
                ]?.toString(),
              ),
              stableBorrowRate: formatAaveRawApyToApr(
                reserveData?.[
                  AaveV2TypeGetReserveDataIndexes.stableBorrowRate
                ]?.toString(),
              ),
              intrinsicYield: additionalYields?.intrinsicYields?.[oracleKey] ?? 0,

              // rewards
              rewards:
                lender === Lender.MERIDIAN &&
                chainId === Chain.TAIKO_ALETHIA &&
                additionalYields?.lenderRewards
                  ? [
                      {
                        asset: '0xa9d23408b9ba935c230493c40c73824df71a0975',
                        depositRate:
                          additionalYields?.lenderRewards[Lender.MERIDIAN]?.[
                            asset
                          ]?.deposit ?? 0,
                        variableBorrowRate:
                          additionalYields?.lenderRewards[Lender.MERIDIAN]?.[
                            asset
                          ]?.borrow ?? 0,
                        stableBorrowRate: 0,
                      },
                    ]
                  : [],
              decimals: Number(
                configData?.[AaveV2TypeGetReserveConfigurationData.decimals],
              ),
              config: {
                [LENDER_MODE_NO_MODE]: {
                  category: LENDER_MODE_NO_MODE,
                  // collateral factors
                  borrowCollateralFactor:
                    Number(
                      configData?.[
                        AaveV2TypeGetReserveConfigurationData.ltv
                      ].toString(),
                    ) / BPS,
                  collateralFactor:
                    Number(
                      configData?.[
                        AaveV2TypeGetReserveConfigurationData
                          .liquidationThreshold
                      ].toString(),
                    ) / BPS,
                  borrowFactor: 1,
                  liquidationPenalty: (() => {
                    const lb = Number(
                      configData?.[
                        AaveV2TypeGetReserveConfigurationData.liquidationBonus
                      ].toString(),
                    )
                    return Number.isFinite(lb) && lb > 0 ? lb / BPS - 1 : 0
                  })(),
                  closeFactor: 0.5,
                  collateralDisabled: !collateralActive,
                  debtDisabled: !borrowingEnabled,
                },
              },
              closeFactor: 0.5,
              liquidationBonus:
                Number(
                  configData?.[
                    AaveV2TypeGetReserveConfigurationData.liquidationBonus
                  ].toString(),
                ) / BPS,
              // flags
              collateralActive: collateralActive,
              borrowingEnabled: borrowingEnabled,
              depositsEnabled: true,
              hasStable:
                configData?.[
                  AaveV2TypeGetReserveConfigurationData.stableBorrowRateEnabled
                ],
              isActive:
                configData?.[AaveV2TypeGetReserveConfigurationData.isActive],
              isFrozen:
                configData?.[AaveV2TypeGetReserveConfigurationData.isFrozen],
            }

            const aaveTokens = aaveTokenMap[asset]

            if (!!aaveTokens) {
              // metadata
              resultReserves[marketUid] = {
                ...resultReserves[marketUid],
                params: {
                  metadata: {
                    aToken: aaveTokens.aToken,
                    vToken: aaveTokens.vToken,
                    sToken: aaveTokens.sToken,
                  },
                },
              }
            }
          }

          return {
            data: resultReserves,
            chainId,
          }
        },
        expectedNumberOfCalls,
      ]
    }
  }
}
